// Splits a foreign-PEP match's category-tagged data into two display buckets:
//   - "reputational" (Orðsporsáhætta): RRE, GRI, POI, INS
//   - "international" (Alþjóðlegir válistar): everything else (PEP, SAN, SOE, DD, REL, ...)
// The backend returns the data flat; this split is purely for the frontend. Nothing is mutated.

import { PepResult } from "../../../../types/PepTypes";
import { ForeignPepReport } from "../../../../types/CompanyAnswersTypes";

export type PepBucket = "international" | "reputational";

// Dataset codes that belong to the reputational bucket. Everything else is international.
const REPUTATIONAL_CODES = ["RRE", "GRI", "POI", "INS"];

// "RRE-CURRENT" -> base code "RRE". Variant suffixes (-CURRENT/-FORMER/-LINKED) ride with their base code.
const isReputationalCode = (token: string) =>
    REPUTATIONAL_CODES.includes(token.split("-")[0].trim().toUpperCase());

const isReputationalCategory = (label: string) => /\((RRE|GRI|POI|INS)\)/.test(label);

// Entry-list fields per bucket. rel/dd/san/pep/soe are international; rre/gri/poi/ins are reputational.
const REPUTATIONAL_ENTRY_KEYS: (keyof PepResult)[] = ["rreEntries", "griEntries", "poiEntries", "insEntries"];
const INTERNATIONAL_ENTRY_KEYS: (keyof PepResult)[] = ["relEntries", "ddEntries", "sanEntries", "pepEntries", "soeEntry"];

// True for a non-empty array, or an object with any non-empty value (handles the sanEntries/pepEntries/soeEntry objects).
const hasData = (v: unknown): boolean =>
    Array.isArray(v) ? v.length > 0
        : v != null && typeof v === "object" ? Object.values(v as Record<string, unknown>).some(hasData)
            : v != null && v !== "";

const codesOf = (match: PepResult) => (match.category ?? "").split("/").map(s => s.trim()).filter(Boolean);

/** True if this single match carries any data in the given bucket (by category code, category label, or entry list). */
export const matchHasBucket = (match: PepResult, bucket: PepBucket): boolean => {
    const codes = codesOf(match);
    const codeHit = bucket === "reputational" ? codes.some(isReputationalCode) : codes.some(c => !isReputationalCode(c));
    if (codeHit) return true;

    const cats = match.categories ?? [];
    const catHit = bucket === "reputational" ? cats.some(isReputationalCategory) : cats.some(c => !isReputationalCategory(c));
    if (catHit) return true;

    const keys = bucket === "reputational" ? REPUTATIONAL_ENTRY_KEYS : INTERNATIONAL_ENTRY_KEYS;
    return keys.some(k => hasData(match[k]));
};

/**
 * Returns the matches that have data in the given bucket, each as a full PepResult (all shared
 * person/company fields kept) but with category / categories narrowed to that bucket. Suitable to
 * pass straight to ForeignPepModal, which renders the summary fields + the (now narrowed) categories.
 */
export const bucketMatches = (matches: PepResult[] | undefined, bucket: PepBucket): PepResult[] => {
    if (!matches) return [];
    return matches
        .filter(m => matchHasBucket(m, bucket))
        .map(m => {
            const narrowedCodes = codesOf(m).filter(c => bucket === "reputational" ? isReputationalCode(c) : !isReputationalCode(c));
            const narrowedCats = (m.categories ?? []).filter(c => bucket === "reputational" ? isReputationalCategory(c) : !isReputationalCategory(c));
            return { ...m, category: narrowedCodes.join("/"), categories: narrowedCats };
        });
};

/** True if the report has at least one match with data in the given bucket. Drives the column's Já/Nei. */
export const reportHasBucket = (report: ForeignPepReport | undefined, bucket: PepBucket): boolean =>
    !!report?.mappedReport?.match?.some(m => matchHasBucket(m, bucket));
